import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Navigate } from "react-router-dom";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { agencySessionKey, getAgencySession, subscribeAgencySession } from "../../../utils/agencySession.ts";
import { AccountGrid, type AccountColumn } from "../../shared/account/AccountGrid.tsx";
import {
  AccountButton, AccountToolbarButton, AccountDrawer, AccountSection, AccountForm, AccountInput, AccountSelect,
  AccountSwitch, useAccountAction, useAccountFeedback,
} from "../../shared/account/AccountUI.tsx";
import { agencyPasswordRules, normalizeAgencyPassword } from "../Auth/agencyPasswordPolicy.ts";
import { normalizeLoginDigits } from "../Auth/customerIdentityService.ts";
import {
  agencyContext, agencyMembers, createAgencyMember, changeAgencyMember, deleteAgencyMember,
  type AgencyMember, type AgencyRole,
} from "../Auth/agencyIdentityService.ts";

function normalizeMobile(value: string) {
  return normalizeLoginDigits(value).trim().replace(/^(?:\+98|0098)/, "0");
}

function roles() {
  return [
    { value: "Manager", label: t("مدیر نمایندگی") },
    { value: "Staff", label: t("پرسنل نمایندگی") },
  ];
}

function optionalAgencyPasswordRules() {
  return [{
    validator: async function (_: unknown, value?: string) {
      if (!value) return;
      await agencyPasswordRules()[0].validator(_, value);
    },
  }];
}

export default function AgencyPage() {
  useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  if (getAgencySession()?.role !== "Manager") return <Navigate to="/account/orders" replace />;
  return <PersonnelWorkspace />;
}

function PersonnelWorkspace() {
  const language = useStoreLanguage();
  const feedback = useAccountFeedback();
  const action = useAccountAction("agency-personnel:" + agencySessionKey());
  const [members, setMembers] = useState<AgencyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState<AgencyMember | "create" | null>(null);
  const [dirty, setDirty] = useState(false);
  const [form] = AccountForm.useForm();
  const mounted = useRef(true);
  const generation = useRef(0);
  const notifyError = feedback.httpError;

  const refresh = useCallback(async function () {
    const revision = ++generation.current;
    const session = agencySessionKey();
    setLoading(true);
    try {
      await agencyContext();
      const data = await agencyMembers();
      if (mounted.current && revision === generation.current && session === agencySessionKey()) setMembers(data);
    } catch (error) {
      if (mounted.current && revision === generation.current) { setMembers([]); notifyError(error); }
    } finally { if (mounted.current && revision === generation.current) setLoading(false); }
  }, [notifyError]);

  useEffect(function () {
    mounted.current = true;
    void refresh();
    return function () { mounted.current = false; };
  }, [refresh, language]);
  useEffect(function () {
    if (!dirty) return;
    function preventUnload(event: BeforeUnloadEvent) { event.preventDefault(); event.returnValue = ""; }
    window.addEventListener("beforeunload", preventUnload);
    return function () { window.removeEventListener("beforeunload", preventUnload); };
  }, [dirty]);

  function openEditor(target: AgencyMember | "create") {
    form.resetFields();
    form.setFieldsValue(target === "create"
      ? { displayName: "", mobile: "", password: "", passwordConfirmation: "", role: "Staff" }
      : { ...target, mobile: target.mobile ?? "", email: target.email ?? "", newPassword: "", passwordConfirmation: "" });
    setDirty(false);
    setEditor(target);
  }
  async function closeEditor() {
    if (action.pending) return false;
    if (dirty && !await feedback.confirm({ title: t("تغییرات ذخیره نشده"), description: t("تغییرات ذخیره نشده کنار گذاشته شود؟") })) return false;
    setEditor(null);
    setDirty(false);
    return true;
  }
  async function save(values: {
    displayName: string;
    mobile: string;
    email: string;
    password: string;
    newPassword: string;
    passwordConfirmation: string;
    role: AgencyRole;
    isActive: boolean;
  }) {
    await action.run(async function () {
      if (!editor) return;
      if (editor === "create") {
        await createAgencyMember({
          displayName: values.displayName.trim(),
          mobile: normalizeMobile(values.mobile),
          password: values.password,
          passwordConfirmation: values.passwordConfirmation,
          role: values.role,
        });
        feedback.success({ title: t("عضو نمایندگی افزوده شد.") });
        setDirty(false);
        setEditor(null);
        await refresh();
      } else {
        await changeAgencyMember(editor, {
          displayName: values.displayName.trim(),
          mobile: normalizeMobile(values.mobile || ""),
          email: values.email?.trim().toLowerCase() || "",
          newPassword: values.newPassword || "",
          passwordConfirmation: values.passwordConfirmation || "",
          role: values.role,
          isActive: values.isActive,
        });
        feedback.success({ title: t("اطلاعات عضو به‌روزرسانی شد.") });
        setDirty(false);
        setEditor(null);
        await refresh();
      }
    });
  }
  function deleteReason(member: AgencyMember) {
    if (String(member.id) === String(getAgencySession()?.membershipId)) return t("حذف عضویت حساب جاری مجاز نیست.");
    if (member.isActive && member.role === "Manager" && members.filter(function (item) { return item.isActive && item.role === "Manager"; }).length <= 1) return t("نمایندگی باید حداقل یک مدیر فعال داشته باشد.");
    return undefined;
  }
  async function remove(member: AgencyMember) {
    await action.run(async function () {
      if (!await feedback.confirm({ title: t("حذف عضو نمایندگی"), description: member.displayName + " — " + t("عضویت این فرد در نمایندگی حذف و دسترسی او قطع می‌شود. ادامه می‌دهید؟"), danger: true, confirmLabel: t("حذف") })) return;
      await deleteAgencyMember(member);
      feedback.success({ title: t("عضو نمایندگی حذف شد.") });
      await refresh();
    });
  }
  const columns = useMemo<AccountColumn<AgencyMember>[]>(function () {
    void language;
    return [
      { colId: "no", headerName: t("ردیف"), valueGetter: "node.rowIndex + 1", sortable: false, filter: false, enableRowGroup: false },
      { field: "displayName", headerName: t("نام عضو"), mobileEssential: true },
      { field: "mobile", headerName: t("شماره همراه"), valueGetter: function (params) { return params.data?.mobile ?? ""; } },
      { field: "email", headerName: t("ایمیل"), valueGetter: function (params) { return params.data?.email ?? ""; } },
      { field: "role", headerName: t("نقش"), mobileEssential: true, valueGetter: function (params) { return roles().find(function (role) { return role.value === params.data?.role; })?.label; } },
      { field: "isActive", headerName: t("وضعیت"), mobileEssential: true, valueGetter: function (params) { return params.data?.isActive ? t("فعال") : t("غیرفعال"); } },
    ];
  }, [language]);
  return (
    <>
      <AccountGrid
        id="agency-members"
        label={t("مدیریت پرسنل")}
        rows={members}
        columns={columns}
        loading={loading}
        busy={action.pending}
        onRefresh={function () { void refresh(); }}
        actions={
          <AccountToolbarButton
            type="primary"
            icon={<Plus size={18} />}
            disabled={loading || action.pending}
            onClick={function () { openEditor("create"); }}
          >
            {t("افزودن عضو")}
          </AccountToolbarButton>
        }
        rowActions={[
          { key: "edit", icon: <PencilSimple size={17} />, label: t("ویرایش"), run: openEditor },
          { key: "delete", icon: <Trash size={17} />, label: t("حذف"), danger: true, disabledReason: deleteReason, run: function (row) { void remove(row); } },
        ]}
      />
      <AccountDrawer
        title={editor === "create" ? t("افزودن عضو نمایندگی") : t("ویرایش اطلاعات عضو")}
        open={editor !== null}
        onClose={closeEditor}
        dirty={dirty}
        busy={action.pending}
        footer={<>
          <AccountButton
            type="primary"
            loading={action.pending}
            onClick={function () { form.submit(); }}
          >
            {editor === "create" ? t("افزودن عضو") : t("ذخیره")}
          </AccountButton>
          <AccountButton
            disabled={action.pending}
            onClick={function () { void closeEditor(); }}
          >
            {t("انصراف")}
          </AccountButton>
        </>}
      >
        <AccountForm
          form={form}
          layout="vertical"
          disabled={action.pending}
          onValuesChange={function () { setDirty(true); }}
          onFinish={save}
          onFinishFailed={function () { feedback.error({ title: t("اطلاعات عضو را کامل کنید.") }); }}
        >
          <AccountSection title={t("اطلاعات حساب")}>
            <AccountForm.Item
              name="displayName"
              label={t("نام و نام خانوادگی")}
              rules={[
                { required: true, whitespace: true, message: t("نام خود را وارد کنید.") },
                { max: 200, message: t("نام بیش از حد طولانی است.") },
              ]}
            >
              <AccountInput autoComplete="name" />
            </AccountForm.Item>
            <AccountForm.Item
              name="mobile"
              label={t("شماره همراه")}
              normalize={normalizeMobile}
              dependencies={["email"]}
              rules={[
                { required: editor === "create", message: t("شماره همراه را وارد کنید.") },
                {
                  validator: async function (_, value: string) {
                    if (!value && editor !== "create" && !form.getFieldValue("email")) {
                      throw new Error(t("حداقل شماره همراه یا ایمیل را وارد کنید."));
                    }
                    if (!value || /^09\d{9}$/.test(normalizeMobile(value))) return;
                    throw new Error(t("شماره همراه معتبر وارد کنید."));
                  },
                },
              ]}
            >
              <AccountInput
                inputMode="tel"
                dir="ltr"
                autoComplete="tel"
                maxLength={16}
              />
            </AccountForm.Item>
            {editor !== "create" ? (
              <AccountForm.Item
                name="email"
                label={t("ایمیل")}
                dependencies={["mobile"]}
                rules={[
                  { type: "email", message: t("آدرس ایمیل معتبر وارد کنید.") },
                  {
                    validator: async function (_, value: string) {
                      if (!value && !form.getFieldValue("mobile")) {
                        throw new Error(t("حداقل شماره همراه یا ایمیل را وارد کنید."));
                      }
                    },
                  },
                ]}
              >
                <AccountInput
                  autoComplete="email"
                  dir="ltr"
                  maxLength={254}
                />
              </AccountForm.Item>
            ) : null}
            {editor === "create" ? (
              <>
                <AccountForm.Item
                  name="password"
                  label={t("رمز عبور")}
                  extra={t("برای حساب جدید، رمزی با حداقل ۱۵ حرف وارد کنید. برای اتصال مجدد حساب موجود، خالی بگذارید.")}
                  rules={optionalAgencyPasswordRules()}
                >
                  <AccountInput.Password
                    dir="ltr"
                    autoComplete="new-password"
                  />
                </AccountForm.Item>
                <AccountForm.Item
                  name="passwordConfirmation"
                  label={t("تکرار رمز عبور")}
                  dependencies={["password"]}
                  rules={[
                    function ({ getFieldValue }) {
                      return {
                        validator: async function (_, value: string) {
                          if (!value && !getFieldValue("password")) return;
                          if (normalizeAgencyPassword(getFieldValue("password")) === normalizeAgencyPassword(value)) return;
                          throw new Error(t("رمز عبور و تکرار آن یکسان نیستند."));
                        },
                      };
                    },
                  ]}
                >
                  <AccountInput.Password
                    dir="ltr"
                    autoComplete="new-password"
                  />
                </AccountForm.Item>
              </>
            ) : editor ? (
              <>
                <AccountForm.Item
                  name="newPassword"
                  label={t("رمز عبور جدید")}
                  extra={t("برای حفظ رمز عبور فعلی، این فیلد را خالی بگذارید.")}
                  rules={optionalAgencyPasswordRules()}
                >
                  <AccountInput.Password
                    dir="ltr"
                    autoComplete="new-password"
                  />
                </AccountForm.Item>
                <AccountForm.Item
                  name="passwordConfirmation"
                  label={t("تکرار رمز عبور جدید")}
                  dependencies={["newPassword"]}
                  rules={[
                    function ({ getFieldValue }) {
                      return {
                        validator: async function (_, value: string) {
                          if (!value && !getFieldValue("newPassword")) return;
                          if (normalizeAgencyPassword(getFieldValue("newPassword")) === normalizeAgencyPassword(value)) return;
                          throw new Error(t("رمز عبور و تکرار آن یکسان نیستند."));
                        },
                      };
                    },
                  ]}
                >
                  <AccountInput.Password
                    dir="ltr"
                    autoComplete="new-password"
                  />
                </AccountForm.Item>
              </>
            ) : null}
          </AccountSection>
          <AccountSection title={t("نقش در نمایندگی")}>
            <AccountForm.Item
              name="role"
              label={t("نقش عضو")}
              rules={[{ required: true }]}
            >
              <AccountSelect options={roles()} />
            </AccountForm.Item>
            {editor !== "create" ? (
              <AccountForm.Item
                name="isActive"
                label={t("دسترسی فعال")}
                valuePropName="checked"
              >
                <AccountSwitch />
              </AccountForm.Item>
            ) : null}
          </AccountSection>
        </AccountForm>
      </AccountDrawer>
    </>
  );
}
